import { ProgressIndicatorType } from "@/types/ProgressIndicator";
import { CopyModelMaterialRequest, Scenario, ScenarioModel } from "@api/apiClient";
import { useCopyModelMaterials } from "@api/scenarios";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import { Box, Button, FormControl, Grid, InputLabel, ListItemText, ListSubheader, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, TextField, Typography } from "@mui/material";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
interface CopyModelMaterialProps {
  scenarios: Scenario[];
  copyScenarioId: string;
  copyModelId: string;
  modelName: string;
  handleCopyModelMaterialClose: () => void;
}

const CopyModelMaterialForm = observer((props: CopyModelMaterialProps) => {
  const appStore = useAppStore();
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>("");
  const [selectedModelId, setSelectedModelId] = useState<string>("");
  const [scenarioModels, setScenarioModels] = useState<ScenarioModel[]>();
  const [isCopyEnabled, setIsCopyEnabled] = useState(false);
  const copyModelMaterials = useCopyModelMaterials();

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    copyModelMaterials.mutateAsync(
      new CopyModelMaterialRequest({
        dstScenarioId: props.copyScenarioId,
        dstModelId: props.copyModelId,
        srcModelId: selectedModelId,
        srcScenarioId: selectedScenarioId,
        modelName: props.modelName,
      }) as any,
    );
    appStore.progressIndicator.setShowProgressIndicator(ProgressIndicatorType.CopyModelMaterials, true);
    props.handleCopyModelMaterialClose();
  };

  const handleScenarioChange = (event: SelectChangeEvent<string>) => {
    setSelectedScenarioId(event.target.value);
    setSelectedModelId("");
    const selectedScenario = props.scenarios.find((scenario) => scenario.scenarioId === event.target.value);
    if (selectedScenario) {
      setScenarioModels(selectedScenario.scenarioModels);
      setSelectedModelId(props.copyModelId);
    }
  };
  const handleModelChange = (event: SelectChangeEvent<string>) => {
    setSelectedModelId(event.target.value);
  };
  useEffect(() => {
    setIsCopyEnabled(!!selectedScenarioId && !!selectedModelId);
  }, [selectedScenarioId, selectedModelId]);

  useEffect(() => {
    const selectedScenario = props.scenarios.find((scenario) => scenario.scenarioId === selectedScenarioId);
    if (selectedScenario) {
      setScenarioModels(selectedScenario.scenarioModels);
      if (props.copyModelId && selectedScenario.scenarioModels?.some((model) => model.modelId === props.copyModelId)) {
        setSelectedModelId(props.copyModelId);
      } else {
        setSelectedModelId("");
      }
    }
  }, [props.copyScenarioId, props.scenarios, selectedScenarioId, props.copyModelId]);

  return (
    <form onSubmit={handleSubmit}>
      <Stack mt={1} spacing={2}>
        <TextField
          label={i18n.t("scenarios-grid.copy-modal-materials-dialog.text-box-destination.scenario-title")}
          name="Scenario"
          value={props.scenarios.find((scenario) => scenario.scenarioId === props.copyScenarioId)?.name}
          margin="normal"
          variant="outlined"
          fullWidth
          slotProps={{
            input: { readOnly: true },
          }}
        />
        <TextField
          label={i18n.t("scenarios-grid.copy-modal-materials-dialog.text-box-destination.model-title")}
          name="Model"
          value={props.modelName}
          margin="normal"
          variant="outlined"
          fullWidth
          slotProps={{
            input: { readOnly: true },
          }}
        />
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">{i18n.t("scenarios-grid.copy-modal-materials-dialog.select-box.scenario")}</InputLabel>

          <Select
            labelId="label-select"
            id="label-select"
            value={selectedScenarioId}
            onChange={handleScenarioChange}
            input={<OutlinedInput label={i18n.t("scenarios-grid.copy-modal-materials-dialog.select-box.scenario")} />}
            MenuProps={MenuProps}
          >
            {Object.values(props.scenarios).map((scenario) => (
              <MenuItem key={scenario.name} value={scenario.scenarioId}>
                {scenario.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="demo-simple-select-label">{i18n.t("scenarios-grid.copy-modal-materials-dialog.select-box.model")}</InputLabel>
          <Select
            labelId="label-select"
            id="label-select"
            value={selectedModelId}
            onChange={handleModelChange}
            input={<OutlinedInput label={i18n.t("scenarios-grid.copy-modal-materials-dialog.select-box.model")} />}
            MenuProps={MenuProps}
          >
            <ListSubheader>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>{i18n.t("scenarios-grid.copy-modal-materials-dialog.model-select-box.model")}</Box>
                <Typography color="text.secondary">{i18n.t("scenarios-grid.copy-modal-materials-dialog.model-select-box.version")} </Typography>
              </Box>
            </ListSubheader>

            {scenarioModels?.map((sm) => (
              <MenuItem key={sm.model?.apsItemName} value={sm.modelId}>
                <ListItemText
                  sx={{
                    "& .MuiListItemText-primary": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    },
                  }}
                >
                  <Box>{sm.model?.apsItemName}</Box>
                  <Typography color="text.secondary">{sm.model?.apsVersion}</Typography>
                </ListItemText>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <Grid container spacing={2} sx={{ justifyContent: "end" }}>
            <Button autoFocus loading={copyModelMaterials.isPending} disabled={!isCopyEnabled} variant="contained" type="submit">
              {i18n.t("scenarios-grid.copy-modal-materials-dialog.copy-button.title")}
            </Button>
          </Grid>
        </Box>
      </Stack>
    </form>
  );
});

export default CopyModelMaterialForm;
